import { SelectProps } from 'antd';
import { IntlShape } from 'react-intl';
import { VFC } from 'react';
import { isNil } from 'rambda';
import { SuggestedSelection, SuggestedValue } from '../../../hooks/meter_models/useMeterModelDetails';
import { SelectMessages as messages } from '../messages';
import { theme } from '../../../consts/theme';

const SuggestedGroupLabel: VFC<{ label: string }> = ({ label }) => (
  <span style={{ fontWeight: 'bold', fontSize: '16px', color: theme.colors.main }}>{label}</span>
);

const BaseGroupLabel: VFC<{ label: string }> = ({ label }) => (
  <span style={{ fontWeight: 'bold', color: theme.colors.dark }}>{label}</span>
);

export interface SelectOptionsWithSuggestionsParams<T> {
  intl: IntlShape;
  values: T[];
  suggestedValue?: SuggestedValue;
  suggestedSelection?: SuggestedSelection;
  labelFormatter?: (value: T) => string;
}

/**
 * Generic function to create select options for enums with suggestion support
 */
export function getSelectOptionsWithSuggestions<T extends string | number>({
  intl,
  values,
  suggestedValue,
  suggestedSelection,
  labelFormatter,
}: SelectOptionsWithSuggestionsParams<T>): SelectProps<T>['options'] {
  const suggested: SelectProps<T>['options'] = [];
  const base: SelectProps<T>['options'] = [];

  (values ?? []).forEach(value => {
    const isSuggestedFromSelection =
      Array.isArray(suggestedSelection) && (suggestedSelection as Array<string | number>).includes(value);

    const isSuggested = (!isNil(suggestedValue) && value === suggestedValue) || isSuggestedFromSelection;
    const label = labelFormatter ? labelFormatter(value) : String(value);

    (isSuggested ? suggested : base).push({ value, label });
  });

  const suggestedOptions = {
    label: <SuggestedGroupLabel label={intl.formatMessage(messages.suggested)} />,
    options: suggested,
  };

  const baseOptions = {
    label: <BaseGroupLabel label={intl.formatMessage(messages.base)} />,
    options: base,
  };

  if (suggested.length > 0 && base.length > 0) {
    return [suggestedOptions, baseOptions];
  }

  return [suggested.length > 0 ? suggestedOptions : baseOptions];
}
